/**
 * 设备管理类型定义
 */

/** 设备类型 */
export type DeviceType = 'light' | 'speaker' | 'screen' | 'sensor' | 'camera'

/** 设备状态 */
export type DeviceStatus = 'online' | 'offline'

/** 设备分类 */
export type DeviceCategory = 'lighting' | 'audio' | 'display' | 'sensor' | 'camera' | 'control'

/** 设备层级（空间位置） */
export enum DeviceLayer {
  FLOOR_L1 = 'floor_l1',           // 一楼地板
  CEILING_L1 = 'ceiling_l1',       // 一楼天花板
  FLOOR_L2 = 'floor_l2',           // 二楼地板
  CEILING_L2 = 'ceiling_l2',       // 二楼天花板
  WALL = 'wall',                  // 墙面
  AMBIENT = 'ambient'              // 环境设备（不定位）
}

/** 设备层级配置 */
export const LAYER_CONFIG: Record<DeviceLayer, {
  name: string
  zIndex: number
  color: string
  icon: string
  description: string
}> = {
  [DeviceLayer.FLOOR_L1]: {
    name: '一楼地板',
    zIndex: 10,
    color: '#4CAF50',
    icon: '🟢',
    description: '安装在一楼地面的设备（如地灯、传感器）'
  },
  [DeviceLayer.CEILING_L1]: {
    name: '一楼天花板',
    zIndex: 20,
    color: '#2196F3',
    icon: '🔵',
    description: '安装在一楼天花板的设备（如吊灯、音响）'
  },
  [DeviceLayer.FLOOR_L2]: {
    name: '二楼地板',
    zIndex: 30,
    color: '#FF9800',
    icon: '🟠',
    description: '安装在二楼地面的设备'
  },
  [DeviceLayer.CEILING_L2]: {
    name: '二楼天花板',
    zIndex: 40,
    color: '#9C27B0',
    icon: '🟣',
    description: '安装在二楼天花板的设备'
  },
  [DeviceLayer.WALL]: {
    name: '墙面',
    zIndex: 15,
    color: '#795548',
    icon: '🟤',
    description: '安装在墙面的设备（如开关、插座、壁灯）'
  },
  [DeviceLayer.AMBIENT]: {
    name: '环境设备',
    zIndex: 5,
    color: '#9E9E9E',
    icon: '⚪',
    description: '不定位的空间设备（如背景音乐、中央空调）'
  },
}

/** 物理位置（网格坐标） */
export interface PhysicalPosition {
  x: number
  y: number
  rotation?: number
}

/** 扩展元数据 */
export interface ExtraMetadata {
  mqtt_topic?: string
  icon?: string
  manufacturer?: string
  model?: string
  firmware_version?: string
  [key: string]: any
}

/** 设备信息 */
export interface Device {
  id: number
  device_id: string
  device_name: string
  device_type: DeviceType
  device_category?: string
  venue_id: number
  room_id: number | null
  location_description?: string
  status: DeviceStatus
  last_seen?: string
  extra_metadata?: ExtraMetadata
  physical_position?: PhysicalPosition | any
  enabled?: boolean
  created_at: string
  updated_at: string
}

/** 平面图配置 */
export interface FloorplanConfig {
  image_url: string
  grid_width: number
  grid_height: number
  uploaded_at: string
}

/** 房间设备详细信息 */
export interface RoomDevicesDetail {
  venue_id: number
  room_id: number
  devices: Device[]
  floorplan?: any
  total: number
  online: number
  offline: number
}

/** 创建设备请求（匹配后端 schema） */
export interface CreateDeviceRequest {
  device_id: string
  device_name: string
  device_category: string  // 后端要求必填
  device_type: DeviceType
  venue_id: number  // 后端要求必填（场馆ID）
  room_id?: number  // 可选，0或undefined表示未分配到房间
  manufacturer?: string
  model?: string
  serial_number?: string
  firmware_version?: string
  location_description?: string
  extra_metadata?: ExtraMetadata
}

/** 更新设备请求 */
export interface UpdateDeviceRequest {
  device_name?: string
  device_category?: string
  device_type?: DeviceType
  venue_id?: number
  room_id?: number
  manufacturer?: string
  model?: string
  serial_number?: string
  firmware_version?: string
  location_description?: string
  extra_metadata?: ExtraMetadata
  enabled?: boolean
}

/** 更新设备布局请求（匹配后端 schema） */
export interface FloorplanPosition {
  grid_x: number
  grid_y: number
  pixel_x?: number
  pixel_y?: number
  rotation?: number
  scale?: number
}

export interface UpdateDeviceLayoutRequest {
  is_deployed: boolean
  floorplan?: FloorplanPosition
  logical_location?: string
}

/** 平面图上传响应 */
export interface FloorplanUploadResponse {
  enabled: boolean
  image_url: string
  image_width: number
  image_height: number
  grid?: any
}

/** API 响应包装 */
export interface ApiResponse<T = any> {
  error_code?: number
  message: string
  data: T
}

/** 批量更新设备请求 */
export interface BatchUpdateDevicesRequest {
  device_ids: number[]           // 设备ID列表
  room_id?: number | null        // 目标房间ID（null或0表示移除到未分配）
  venue_id?: number              // 目标场馆ID（如果需要变更场馆）
}

/** 批量更新设备响应 */
export interface BatchUpdateDevicesResponse {
  updated: number                // 成功更新的数量
  failed: number                 // 失败的数量
  errors: Array<{                // 错误详情
    device_id: number
    error: string
  }>
}
